import React, { useEffect, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import SeoHead from '@components/SeoHead';
import GameDetailView from '@components/GameDetailView';
import { getGameBySlug } from '@lib/content';
import { buildGameSchema, buildGameBreadcrumbSchema, buildFAQSchema, buildHowToSchema, combineSchemas } from '@lib/jsonld';
import categories from '@content/categories.json';
import { siteConfig } from '../config';
import { useGameRecommendations } from '@hooks/useGameRecommendations';
import { useGamePlayer } from '@hooks/useGamePlayer';

const GamePage: React.FC = () => {
  const { slug } = useParams();

  // In single-game mode, /{gameSlug} is a duplicate of /. 301 redirect to consolidate PageRank.
  if (siteConfig.siteMode === 'single-game' && slug === siteConfig.gameSlug) {
    return <Navigate to="/" replace />;
  }

  const game = slug ? getGameBySlug(slug) : null;
  
  const {
    iframeRef,
    gamePlayerRef,
    showBackToGame,
    gameStarted,
    isLoading,
    isTheaterMode,
    handlePlayClick,
    handleIframeLoad,
    scrollToGame,
    toggleTheaterMode,
  } = useGamePlayer({ gameSlug: slug });

  const { sidebarGames, moreGames } = useGameRecommendations(game ?? null);

  if (!slug || !game) {
    return <p className="muted">Game not found.</p>;
  }

  // Get primary category for breadcrumb (first in categories array)
  const primaryCategory = game.categories?.[0];
  const currentCategory = primaryCategory ? categories.find(c => c.slug === primaryCategory) : null;

  const jsonLdSchemas = useMemo(() => {
    const schemas: any[] = [
      buildGameSchema(game),
      buildGameBreadcrumbSchema(game, currentCategory?.name, currentCategory?.slug)
    ];
    
    // Add FAQ schema if game has FAQs
    if (game.faqs && game.faqs.length > 0) {
      const faqSchema = buildFAQSchema(game.faqs);
      if (faqSchema) schemas.push(faqSchema);
    }

    // Add HowTo schema if game has howToPlay text
    if (game.howToPlay) {
      const howToSchema = buildHowToSchema(game);
      if (howToSchema) schemas.push(howToSchema);
    }
    
    return combineSchemas(...schemas);
  }, [game, currentCategory]);

  return (
    <>
      <SeoHead
        title={game.seoTitle || game.title}
        description={game.description}
        image={game.thumbnail}
        canonicalUrl={`${siteConfig.siteUrl}/${slug}`}
        jsonLd={jsonLdSchemas}
      />
      <GameDetailView
        game={game}
        slug={slug}
        gameStarted={gameStarted}
        isLoading={isLoading}
        showBackToGame={showBackToGame}
        sidebarGames={sidebarGames}
        moreGames={moreGames}
        iframeRef={iframeRef}
        gamePlayerRef={gamePlayerRef}
        onPlayClick={handlePlayClick}
        onIframeLoad={handleIframeLoad}
        onScrollToGame={scrollToGame}
        isTheaterMode={isTheaterMode}
        onTheaterModeToggle={toggleTheaterMode}
      />
    </>
  );
};

export default GamePage;
